import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CreateDentistaData, Dentista } from '@/hooks/useDentistas';

interface DentistaFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateDentistaData) => Promise<boolean | Dentista | null>;
  dentista?: Dentista;
  title: string;
}

const especialidades = [
  'Clínica Geral',
  'Ortodontia',
  'Endodontia',
  'Periodontia',
  'Implantodontia',
  'Cirurgia Oral',
  'Prótese',
  'Odontopediatria',
  'Radiologia',
  'Patologia Oral'
];

export function DentistaForm({ open, onOpenChange, onSubmit, dentista, title }: DentistaFormProps) {
  const [formData, setFormData] = useState<CreateDentistaData>({
    nome: dentista?.nome || '',
    cro: dentista?.cro || '',
    especialidade: dentista?.especialidade || '',
    telefone: dentista?.telefone || '',
    email: dentista?.email || '',
    cpf: dentista?.cpf || '',
    endereco: dentista?.endereco || '',
    data_nascimento: dentista?.data_nascimento || '',
    recebe_meta: dentista?.recebe_meta || false,
    meta_periodo: dentista?.meta_periodo || 'Mensal',
    meta_inicio: dentista?.meta_inicio || '',
    meta_fim: dentista?.meta_fim || '',
    meta_valor: Number(dentista?.meta_valor || 0),
    meta_bonus: Number(dentista?.meta_bonus || 0),
    recebe_comissao: dentista?.recebe_comissao || false,
    percentual_comissao: Number(dentista?.percentual_comissao || 0),
    status: dentista?.status || 'Ativo'
  });
  const [loading, setLoading] = useState(false);
  
  // Update form data when dentista prop changes
  useEffect(() => {
    if (dentista) {
      setFormData({
        nome: dentista.nome || '',
        cro: dentista.cro || '',
        especialidade: dentista.especialidade || '',
        telefone: dentista.telefone || '',
        email: dentista.email || '',
        cpf: dentista.cpf || '',
        endereco: dentista.endereco || '',
        data_nascimento: dentista.data_nascimento || '',
        recebe_meta: dentista.recebe_meta || false,
        meta_periodo: dentista.meta_periodo || 'Mensal',
        meta_inicio: dentista.meta_inicio || '',
        meta_fim: dentista.meta_fim || '',
        meta_valor: Number(dentista.meta_valor || 0),
        meta_bonus: Number(dentista.meta_bonus || 0),
        recebe_comissao: dentista.recebe_comissao || false,
        percentual_comissao: Number(dentista.percentual_comissao || 0),
        status: dentista.status || 'Ativo'
      });
    } else {
      setFormData({
        nome: '',
        cro: '',
        especialidade: '',
        telefone: '',
        email: '',
        cpf: '',
        endereco: '',
        data_nascimento: '',
        recebe_meta: false,
        meta_periodo: 'Mensal',
        meta_inicio: '',
        meta_fim: '',
        meta_valor: 0,
        meta_bonus: 0,
        recebe_comissao: false,
        percentual_comissao: 0,
        status: 'Ativo'
      });
    }
  }, [dentista]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.especialidade) return;
    setLoading(true);
    
    const success = await onSubmit(formData);
    
    if (success) {
      onOpenChange(false);
      setFormData({
        nome: '',
        cro: '',
        especialidade: '',
        telefone: '',
        email: '',
        cpf: '',
        endereco: '',
        data_nascimento: '',
        recebe_meta: false,
        meta_periodo: 'Mensal',
        meta_inicio: '',
        meta_fim: '',
        meta_valor: 0,
        meta_bonus: 0,
        recebe_comissao: false,
        percentual_comissao: 0,
        status: 'Ativo'
      });
    }
    
    setLoading(false);
  };

  const handleChange = (field: keyof CreateDentistaData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                required
                placeholder="Nome completo"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cro">CRO *</Label>
              <Input
                id="cro"
                value={formData.cro}
                onChange={(e) => handleChange('cro', e.target.value)}
                required
                placeholder="Ex: CRO-SP 12345"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="especialidade">Especialidade *</Label>
              <Select value={formData.especialidade} onValueChange={(value) => handleChange('especialidade', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a especialidade" />
                </SelectTrigger>
                <SelectContent>
                  {especialidades.map((esp) => (
                    <SelectItem key={esp} value={esp}>{esp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value as 'Ativo' | 'Inativo')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone *</Label>
              <Input
                id="telefone"
                value={formData.telefone}
                onChange={(e) => handleChange('telefone', e.target.value)}
                required
                placeholder="(11) 99999-9999"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                placeholder="email@exemplo.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={formData.cpf}
                onChange={(e) => handleChange('cpf', e.target.value)}
                required
                placeholder="000.000.000-00"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de Nascimento</Label>
              <Input
                id="data_nascimento"
                type="date"
                value={formData.data_nascimento}
                onChange={(e) => handleChange('data_nascimento', e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="endereco">Endereço</Label>
            <Textarea
              id="endereco"
              value={formData.endereco}
              onChange={(e) => handleChange('endereco', e.target.value)}
              placeholder="Endereço completo"
              rows={3}
            />
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <h3 className="font-medium">Meta</h3>
              <p className="text-sm text-muted-foreground">Configure alvo, prazo e bônus de meta deste dentista.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Recebe meta?</Label>
                <Select value={formData.recebe_meta ? 'sim' : 'nao'} onValueChange={(value) => handleChange('recebe_meta', value === 'sim')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">Não</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Período</Label>
                <Select value={formData.meta_periodo || 'Mensal'} onValueChange={(value) => handleChange('meta_periodo', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Diária">Diária</SelectItem>
                    <SelectItem value="Semanal">Semanal</SelectItem>
                    <SelectItem value="Mensal">Mensal</SelectItem>
                    <SelectItem value="Personalizada">Personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Início</Label>
                <Input type="date" value={formData.meta_inicio || ''} onChange={(e) => handleChange('meta_inicio', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Fim</Label>
                <Input type="date" value={formData.meta_fim || ''} onChange={(e) => handleChange('meta_fim', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Valor da meta (R$)</Label>
                <MoneyInput value={formData.meta_valor || 0} onValueChange={(meta_valor) => handleChange('meta_valor', meta_valor)} />
              </div>
              <div className="space-y-2">
                <Label>Bônus ao bater meta (R$)</Label>
                <MoneyInput value={formData.meta_bonus || 0} onValueChange={(meta_bonus) => handleChange('meta_bonus', meta_bonus)} />
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <h3 className="font-medium">Comissão</h3>
              <p className="text-sm text-muted-foreground">Defina se este dentista recebe comissão e qual percentual será aplicado sobre serviços realizados.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Recebe comissão?</Label>
                <Select value={formData.recebe_comissao ? 'sim' : 'nao'} onValueChange={(value) => handleChange('recebe_comissao', value === 'sim')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">Não</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Percentual de comissão (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.percentual_comissao || 0}
                  onChange={(e) => handleChange('percentual_comissao', Number(e.target.value || 0))}
                />
              </div>
            </div>
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              Exemplo: se realizar R$ 1.000,00 e a comissão for {Number(formData.percentual_comissao || 0)}%, recebe{' '}
              {(1000 * Number(formData.percentual_comissao || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.
            </div>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
